import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  CircularProgress,
  Chip,
  Pagination,
} from '@mui/material';
import { Edit as EditIcon, Close as CloseIcon, ArrowBack as ArrowBackIcon, ArrowUpward as ArrowUpwardIcon, ArrowDownward as ArrowDownwardIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../components/common/PageLayout';
import { get, put } from '../utils/api';

export default function VenkateswaraSahasranamaMalaReportPage() {
  const navigate = useNavigate();
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  
  // Filters
  const [nameFilter, setNameFilter] = React.useState('');
  const [initialFilter, setInitialFilter] = React.useState('');
  const [coinNoFilter, setCoinNoFilter] = React.useState('');
  const [engravedFilter, setEngravedFilter] = React.useState('all'); // 'all', 'yes', 'no'
  const [pendingEngraveFilter, setPendingEngraveFilter] = React.useState(false);
  const [yearFilter, setYearFilter] = React.useState('all'); // 'all' or year like '2024', '2025'
  
  // Pagination
  const [page, setPage] = React.useState(1);
  const [perPage, setPerPage] = React.useState(25);
  const [total, setTotal] = React.useState(0);
  
  // Sorting
  const [sortField, setSortField] = React.useState('coinno');
  const [sortDirection, setSortDirection] = React.useState('asc'); // 'asc' or 'desc'
  
  // Stats
  const [stats, setStats] = React.useState({ total: 0, engraved: 0, available: 0, pending_engrave: 0 });
  
  // Edit dialog
  const [editOpen, setEditOpen] = React.useState(false);
  const [editingRecord, setEditingRecord] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState('');
  const [saveSuccess, setSaveSuccess] = React.useState('');

  // Debounce search inputs - only set if minimum characters are met
  const [debouncedNameFilter, setDebouncedNameFilter] = React.useState('');
  const [debouncedInitialFilter, setDebouncedInitialFilter] = React.useState('');

  React.useEffect(() => {
    const timer = setTimeout(() => {
      // Only set debounced value if minimum 3 characters are typed, otherwise clear it
      if (nameFilter.trim().length >= 3) {
        setDebouncedNameFilter(nameFilter.trim());
        setPage(1); // Reset to first page on search
      } else {
        setDebouncedNameFilter(''); // Clear if less than minimum
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [nameFilter]);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      // Only set debounced value if minimum 2 characters are typed, otherwise clear it
      if (initialFilter.trim().length >= 2) {
        setDebouncedInitialFilter(initialFilter.trim());
        setPage(1); // Reset to first page on search
      } else {
        setDebouncedInitialFilter(''); // Clear if less than minimum
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [initialFilter]);

  // Load stats
  React.useEffect(() => {
    (async () => {
      try {
        const res = await get('/ldb/reports/venkateswara-sahasranama-mala/stats');
        console.log('Stats response:', res);
        if (res?.stats) {
          setStats({
            total: Number(res.stats.total) || 0,
            engraved: Number(res.stats.engraved) || 0,
            available: Number(res.stats.available) || 0,
            pending_engrave: Number(res.stats.pending_engrave) || 0,
          });
        } else {
          console.warn('Stats response missing stats property:', res);
          setStats({ total: 0, engraved: 0, available: 0, pending_engrave: 0 });
        }
      } catch (e) {
        console.error('Failed to load stats:', e);
        setStats({ total: 0, engraved: 0, available: 0, pending_engrave: 0 });
      }
    })();
  }, []);

  // Load data with server-side filtering, pagination, and sorting
  React.useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError('');
        
        const params = {
          page: page.toString(),
          per_page: perPage.toString(),
          sort_by: sortField,
          sort_dir: sortDirection,
        };
        
        // Only add search params if they meet minimum length requirements
        if (debouncedNameFilter.trim().length >= 3) {
          params.q = debouncedNameFilter.trim();
        }
        if (debouncedInitialFilter.trim().length >= 2) {
          params.initial = debouncedInitialFilter.trim();
        }
        if (coinNoFilter.trim().length > 0) {
          params.coinno = coinNoFilter.trim();
        }
        if (pendingEngraveFilter) {
          params.pending_engrave = 'true';
        } else if (engravedFilter !== 'all') {
          params.engraved = engravedFilter;
        }
        if (yearFilter !== 'all') {
          params.year_engraved = yearFilter;
        }
        
        const res = await get('/ldb/reports/venkateswara-sahasranama-mala', params);
        const records = Array.isArray(res?.data) ? res.data : [];
        setData(records);
        setTotal(res?.meta?.total || 0);
      } catch (e) {
        setError(e?.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    })();
  }, [page, perPage, debouncedNameFilter, debouncedInitialFilter, coinNoFilter, engravedFilter, pendingEngraveFilter, yearFilter, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setPage(1); // Reset to first page on sort
  };

  const handleEdit = (record) => {
    setEditingRecord({ ...record });
    setEditOpen(true);
    setSaveError('');
    setSaveSuccess('');
  };

  const handleSave = async () => {
    if (!editingRecord) return;
    
    // Validate initials on the coin: if Engraved is Yes, must be 1-3 characters
    if (editingRecord.engraved === 'Yes') {
      if (!editingRecord.initial || editingRecord.initial.trim().length === 0) {
        setSaveError('Initials on the coin is required when Engraved is Yes (1-3 characters)');
        return;
      }
      const trimmedInitial = editingRecord.initial.trim();
      if (trimmedInitial.length < 1) {
        setSaveError('Initials on the coin must have at least 1 character (spaces don\'t count)');
        return;
      }
      if (trimmedInitial.length > 3) {
        setSaveError('Initials on the coin must not exceed 3 characters');
        return;
      }
    }
    
    try {
      setSaving(true);
      setSaveError('');
      
      // Prepare payload with all fields explicitly
      const payload = {
        firstname: editingRecord.firstname || null,
        lastname: editingRecord.lastname || null,
        sponsorname: editingRecord.sponsorname || null,
        honorname: editingRecord.honorname || null,
        initial: editingRecord.initial || null,
        engraved: editingRecord.engraved || null,
        date_engraved: editingRecord.date_engraved || null,
        pageno: editingRecord.pageno || null,
        otherids: editingRecord.otherids || null,
        otherids2: editingRecord.otherids2 || null,
        otherids3: editingRecord.otherids3 || null,
        comments: editingRecord.comments || null, // Explicitly include comments
      };
      
      await put(`/ldb/reports/venkateswara-sahasranama-mala/${editingRecord.id}`, payload);
      
      // Reload stats
      const statsRes = await get('/ldb/reports/venkateswara-sahasranama-mala/stats');
      setStats(statsRes?.stats || { total: 0, engraved: 0, available: 0, pending_engrave: 0 });
      
      // Reload data with current filters
      const params = {
        page: page.toString(),
        per_page: perPage.toString(),
        sort_by: sortField,
        sort_dir: sortDirection,
      };
      if (debouncedNameFilter.trim().length >= 3) {
        params.q = debouncedNameFilter.trim();
      }
      if (debouncedInitialFilter.trim().length >= 2) {
        params.initial = debouncedInitialFilter.trim();
      }
      if (engravedFilter !== 'all') {
        params.engraved = engravedFilter;
      }
      const res = await get('/ldb/reports/venkateswara-sahasranama-mala', params);
      const records = Array.isArray(res?.data) ? res.data : [];
      setData(records);
      setTotal(res?.meta?.total || 0);
      
      // Show success message and keep dialog open
      setSaveSuccess('Record saved successfully!');
      setSaveError('');
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSaveSuccess('');
      }, 5000);
    } catch (e) {
      setSaveError(e?.message || 'Failed to save');
      setSaveSuccess('');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseEdit = () => {
    if (!saving) {
      setEditOpen(false);
      setEditingRecord(null);
      setSaveError('');
      setSaveSuccess('');
    }
  };

  return (
    <PageLayout contentMaxWidth="xl" showHeaderImage={false} showOrgHeader>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate('/manage')} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            Venkateswara Sahasranama Mala
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage Venkateswara Sahasranama Mala records
          </Typography>
        </Box>
      </Box>

      {/* Filters and Stats */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} md={8}>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Name (First, Last, or Honor)"
                  value={nameFilter}
                  onChange={(e) => setNameFilter(e.target.value)}
                  placeholder="Type 3+ characters..."
                  helperText={nameFilter.trim().length > 0 && nameFilter.trim().length < 3 ? 'Minimum 3 characters required' : ''}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Initials on the coin"
                  value={initialFilter}
                  onChange={(e) => setInitialFilter(e.target.value)}
                  placeholder="Type 2+ characters..."
                  helperText={initialFilter.trim().length > 0 && initialFilter.trim().length < 2 ? 'Minimum 2 characters required' : ''}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Coin #"
                  value={coinNoFilter}
                  onChange={(e) => {
                    setCoinNoFilter(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Enter coin number..."
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel 
                    id="engraved-filter-label" 
                    shrink
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'visible',
                      textOverflow: 'clip',
                      maxWidth: '100%',
                    }}
                  >
                    Engraved
                  </InputLabel>
                  <Select
                    labelId="engraved-filter-label"
                    value={engravedFilter}
                    onChange={(e) => setEngravedFilter(e.target.value)}
                    label="Engraved"
                    notched
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="yes">Yes</MenuItem>
                    <MenuItem value="no">No</MenuItem>
                    <MenuItem value="empty">-</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="year-filter-label" shrink>Year Engraved</InputLabel>
                  <Select
                    labelId="year-filter-label"
                    value={yearFilter}
                    onChange={(e) => {
                      setYearFilter(e.target.value);
                      setPage(1);
                    }}
                    label="Year Engraved"
                    notched
                  >
                    <MenuItem value="all">All Years</MenuItem>
                    {Array.from({ length: 10 }, (_, i) => {
                      const year = new Date().getFullYear() - i;
                      return (
                        <MenuItem key={year} value={year.toString()}>
                          {year}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2, pl: { xs: 0, md: 2 }, borderLeft: { xs: 'none', md: '1px solid' }, borderColor: 'divider', flexWrap: 'wrap' }}>
                <Box 
                  onClick={() => {
                    setNameFilter('');
                    setInitialFilter('');
                    setEngravedFilter('all');
                    setPendingEngraveFilter(false);
                    setYearFilter('all');
                    setPage(1);
                  }}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                  px: 1,
                  py: 0.5,
                  borderRadius: 0.5,
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              >
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>Total:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>{stats.total}</Typography>
              </Box>
                <Box 
                  onClick={() => {
                    setNameFilter('');
                    setInitialFilter('');
                    setEngravedFilter('yes');
                    setPendingEngraveFilter(false);
                    setYearFilter('all');
                    setPage(1);
                  }}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                  px: 1,
                  py: 0.5,
                  borderRadius: 0.5,
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              >
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>Engraved:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>{stats.engraved}</Typography>
              </Box>
                <Box 
                  onClick={() => {
                    setNameFilter('');
                    setInitialFilter('');
                    setEngravedFilter('no');
                    setPendingEngraveFilter(false);
                    setYearFilter('all');
                    setPage(1);
                  }}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                  px: 1,
                  py: 0.5,
                  borderRadius: 0.5,
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              >
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>Available:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'info.main' }}>{stats.available}</Typography>
              </Box>
                <Box 
                  onClick={() => {
                    setNameFilter('');
                    setInitialFilter('');
                    setEngravedFilter('all');
                    setPendingEngraveFilter(true);
                    setYearFilter('all');
                    setPage(1);
                  }}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                  px: 1,
                  py: 0.5,
                  borderRadius: 0.5,
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              >
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>Pending Engrave:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'warning.main' }}>{stats.pending_engrave}</Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
        
        {(nameFilter || initialFilter || engravedFilter !== 'all' || pendingEngraveFilter || yearFilter !== 'all') && (
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {nameFilter && (
              <Chip
                label={`Name: ${nameFilter}`}
                onDelete={() => setNameFilter('')}
                size="small"
              />
            )}
            {initialFilter && initialFilter.trim().length >= 2 && (
              <Chip
                label={`Initials on the coin: ${initialFilter}`}
                onDelete={() => setInitialFilter('')}
                size="small"
              />
            )}
            {pendingEngraveFilter && (
              <Chip
                label="Pending Engrave"
                onDelete={() => setPendingEngraveFilter(false)}
                size="small"
              />
            )}
            {!pendingEngraveFilter && engravedFilter !== 'all' && (
              <Chip
                label={`Engraved: ${engravedFilter === 'empty' ? '-' : engravedFilter}`}
                onDelete={() => setEngravedFilter('all')}
                size="small"
              />
            )}
            {yearFilter !== 'all' && (
              <Chip
                label={`Year Engraved: ${yearFilter}`}
                onDelete={() => setYearFilter('all')}
                size="small"
              />
            )}
          </Box>
        )}
        {engravedFilter === 'no' && !pendingEngraveFilter && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Engraved: No includes Available + Pending Engrave ({stats.available} + {stats.pending_engrave} = {stats.available + stats.pending_engrave}).
          </Typography>
        )}
      </Paper>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
      )}

      {/* Table */}
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Pagination Top */}
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Showing {data.length} of {total} records (Page {page} of {Math.ceil(total / perPage)})
              </Typography>
              <Pagination
                count={Math.ceil(total / perPage)}
                page={page}
                onChange={(event, value) => setPage(value)}
                color="primary"
                size="small"
                showFirstButton
                showLastButton
              />
            </Box>

            {/* Header */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '80px 100px 120px 150px 200px 120px 120px 150px 1fr 80px', bgcolor: 'action.hover', borderBottom: '2px solid', borderColor: 'divider' }}>
              <Box sx={{ p: 1.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>Sno</Typography>
              </Box>
              <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }} onClick={() => handleSort('coinno')}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>Coin #</Typography>
                {sortField === 'coinno' && (sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 14 }} /> : <ArrowDownwardIcon sx={{ fontSize: 14 }} />)}
              </Box>
              <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }} onClick={() => handleSort('initial')}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>Initials on the coin</Typography>
                {sortField === 'initial' && (sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 14 }} /> : <ArrowDownwardIcon sx={{ fontSize: 14 }} />)}
              </Box>
              <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }} onClick={() => handleSort('honorname')}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>Honor Name</Typography>
                {sortField === 'honorname' && (sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 14 }} /> : <ArrowDownwardIcon sx={{ fontSize: 14 }} />)}
              </Box>
              <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }} onClick={() => handleSort('sponsorname')}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>Sponsor Name</Typography>
                {sortField === 'sponsorname' && (sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 14 }} /> : <ArrowDownwardIcon sx={{ fontSize: 14 }} />)}
              </Box>
              <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }} onClick={() => handleSort('engraved')}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>Engraved</Typography>
                {sortField === 'engraved' && (sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 14 }} /> : <ArrowDownwardIcon sx={{ fontSize: 14 }} />)}
              </Box>
              <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }} onClick={() => handleSort('date_engraved')}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>Date Engraved</Typography>
                {sortField === 'date_engraved' && (sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 14 }} /> : <ArrowDownwardIcon sx={{ fontSize: 14 }} />)}
              </Box>
              <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }} onClick={() => handleSort('location')}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>Location</Typography>
                {sortField === 'location' && (sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 14 }} /> : <ArrowDownwardIcon sx={{ fontSize: 14 }} />)}
              </Box>
              <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }} onClick={() => handleSort('namam')}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>Namam</Typography>
                {sortField === 'namam' && (sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 14 }} /> : <ArrowDownwardIcon sx={{ fontSize: 14 }} />)}
              </Box>
              <Typography variant="caption" sx={{ p: 1.5, fontWeight: 700, textAlign: 'center' }}>Action</Typography>
            </Box>

            {/* Rows */}
            <Box>
              {data.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No records found
                  </Typography>
                </Box>
              ) : (
                data.map((record, idx) => {
                  const serialNumber = (page - 1) * perPage + idx + 1;
                  return (
                    <Box
                      key={record.id}
                      onClick={() => handleEdit(record)}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '80px 100px 120px 150px 200px 120px 120px 150px 1fr 80px',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        bgcolor: idx % 2 === 0 ? 'transparent' : 'action.hover',
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: 'action.selected',
                        },
                      }}
                    >
                      <Typography variant="body2" sx={{ p: 1.5 }}>{serialNumber}</Typography>
                    <Typography variant="body2" sx={{ p: 1.5 }}>{record.coinno || '—'}</Typography>
                    <Typography variant="body2" sx={{ p: 1.5 }}>{record.initial || '—'}</Typography>
                    <Typography variant="body2" sx={{ p: 1.5 }}>{record.honorname || '—'}</Typography>
                    <Typography variant="body2" sx={{ p: 1.5 }}>{record.sponsorname || '—'}</Typography>
                    <Typography variant="body2" sx={{ p: 1.5 }}>{record.engraved || '—'}</Typography>
                    <Typography variant="body2" sx={{ p: 1.5 }}>
                      {record.date_engraved && record.date_engraved !== '0000-00-00' 
                        ? new Date(record.date_engraved).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
                        : '—'}
                    </Typography>
                    <Typography variant="body2" sx={{ p: 1.5 }}>{record.location || '—'}</Typography>
                    <Typography variant="body2" sx={{ p: 1.5, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                      {record.namam || '—'}
                    </Typography>
                    <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(record);
                        }}
                        sx={{ color: 'primary.main' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  );
                })
              )}
            </Box>

            {/* Summary and Pagination */}
            <Box sx={{ p: 2, borderTop: '2px solid', borderColor: 'divider', bgcolor: 'action.hover', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Showing {data.length} of {total} records (Page {page} of {Math.ceil(total / perPage)})
              </Typography>
              <Pagination
                count={Math.ceil(total / perPage)}
                page={page}
                onChange={(event, value) => setPage(value)}
                color="primary"
                size="small"
                showFirstButton
                showLastButton
              />
            </Box>
          </>
        )}
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={handleCloseEdit} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
              <Typography variant="h6">Edit Record</Typography>
              {saveSuccess && (
                <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
                  {saveSuccess}
                </Typography>
              )}
              {saveError && (
                <Typography variant="body2" color="error.main" sx={{ fontWeight: 500 }}>
                  {saveError}
                </Typography>
              )}
            </Box>
            <IconButton 
              onClick={handleCloseEdit} 
              size="small"
              sx={{
                bgcolor: 'error.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'error.dark',
                },
                width: 32,
                height: 32,
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          {editingRecord && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Coin Details Section */}
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary', pb: 1, borderBottom: '2px solid', borderColor: 'primary.main' }}>
                  Coin Details
                </Typography>
                <Box sx={{ pl: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 2fr 1fr' }, gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Coin #"
                    value={editingRecord.coinno || ''}
                    InputProps={{ readOnly: true }}
                    variant="standard"
                    size="small"
                  />
                  <TextField
                    fullWidth
                    label="Namam"
                    value={editingRecord.namam || ''}
                    InputProps={{ readOnly: true }}
                    variant="standard"
                    size="small"
                  />
                  <TextField
                    fullWidth
                    label="Location"
                    value={editingRecord.location || ''}
                    InputProps={{ readOnly: true }}
                    variant="standard"
                    size="small"
                  />
                </Box>
              </Box>

              <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 3 }} />

              {/* Sponsor Details Section */}
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary', pb: 1, borderBottom: '2px solid', borderColor: 'primary.main' }}>
                  Sponsor Details
                </Typography>
                <Box sx={{ pl: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Name Group */}
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, mb: 1, display: 'block' }}>
                      Name Information
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                      <TextField
                        fullWidth
                        label="First Name"
                        value={editingRecord.firstname || ''}
                        onChange={(e) => setEditingRecord({ ...editingRecord, firstname: e.target.value })}
                        variant="outlined"
                        size="small"
                      />
                      <TextField
                        fullWidth
                        label="Last Name"
                        value={editingRecord.lastname || ''}
                        onChange={(e) => setEditingRecord({ ...editingRecord, lastname: e.target.value })}
                        variant="outlined"
                        size="small"
                      />
                      <TextField
                        fullWidth
                        label="Sponsor Name"
                        value={editingRecord.sponsorname || ''}
                        onChange={(e) => setEditingRecord({ ...editingRecord, sponsorname: e.target.value })}
                        variant="outlined"
                        size="small"
                      />
                      <TextField
                        fullWidth
                        label="Honor Name"
                        value={editingRecord.honorname || ''}
                        onChange={(e) => setEditingRecord({ ...editingRecord, honorname: e.target.value })}
                        variant="outlined"
                        size="small"
                      />
                    </Box>
                  </Box>

                  {/* Engraving Group */}
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, mb: 1, display: 'block' }}>
                      Engraving Information
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
                      <TextField
                        fullWidth
                        label="Initials on the coin"
                        value={editingRecord.initial || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Limit to max 3 characters
                          if (value.length <= 3) {
                            setEditingRecord({ ...editingRecord, initial: value });
                          }
                        }}
                        variant="outlined"
                        size="small"
                        inputProps={{ maxLength: 3 }}
                        error={
                          editingRecord.engraved === 'Yes' &&
                          editingRecord.initial && 
                          editingRecord.initial.length > 0 && 
                          editingRecord.initial.trim().length === 0
                        }
                        helperText={
                          editingRecord.engraved === 'Yes'
                            ? (editingRecord.initial && editingRecord.initial.length > 0 && editingRecord.initial.trim().length === 0
                                ? 'Minimum 1 character required (spaces don\'t count)'
                                : '1-3 characters required when Engraved is Yes')
                            : '1-3 characters (optional)'
                        }
                      />
                      <FormControl fullWidth size="small">
                        <InputLabel>Engraved</InputLabel>
                        <Select
                          value={editingRecord.engraved || ''}
                          onChange={(e) => setEditingRecord({ ...editingRecord, engraved: e.target.value })}
                          label="Engraved"
                        >
                          <MenuItem value="">—</MenuItem>
                          <MenuItem value="Yes">Yes</MenuItem>
                          <MenuItem value="No">No</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        fullWidth
                        label="Date Engraved"
                        type="date"
                        value={editingRecord.date_engraved ? (typeof editingRecord.date_engraved === 'string' ? editingRecord.date_engraved.split('T')[0] : new Date(editingRecord.date_engraved).toISOString().split('T')[0]) : ''}
                        onChange={(e) => setEditingRecord({ ...editingRecord, date_engraved: e.target.value || null })}
                        variant="outlined"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Box>
                  </Box>

                  {/* Additional Information Group */}
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, mb: 1, display: 'block' }}>
                      Additional Information
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
                      <TextField
                        fullWidth
                        label="Page No"
                        value={editingRecord.pageno || ''}
                        onChange={(e) => setEditingRecord({ ...editingRecord, pageno: e.target.value })}
                        variant="outlined"
                        size="small"
                      />
                      <TextField
                        fullWidth
                        label="Other IDs"
                        value={editingRecord.otherids || ''}
                        onChange={(e) => setEditingRecord({ ...editingRecord, otherids: e.target.value })}
                        variant="outlined"
                        size="small"
                      />
                      <TextField
                        fullWidth
                        label="Other IDs 2"
                        value={editingRecord.otherids2 || ''}
                        onChange={(e) => setEditingRecord({ ...editingRecord, otherids2: e.target.value })}
                        variant="outlined"
                        size="small"
                      />
                      <TextField
                        fullWidth
                        label="Other IDs 3"
                        value={editingRecord.otherids3 || ''}
                        onChange={(e) => setEditingRecord({ ...editingRecord, otherids3: e.target.value })}
                        variant="outlined"
                        size="small"
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 3 }} />

              {/* Comments Section */}
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary', pb: 1, borderBottom: '2px solid', borderColor: 'primary.main' }}>
                  Comments
                </Typography>
                <Box sx={{ pl: 2 }}>
                  <TextField
                    fullWidth
                    label="Comments"
                    value={editingRecord.comments || ''}
                    onChange={(e) => setEditingRecord({ ...editingRecord, comments: e.target.value })}
                    variant="outlined"
                    size="small"
                    multiline
                    rows={5}
                    placeholder="Enter any additional comments or notes..."
                  />
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
}

